import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard,
  CalendarDays,
  Plane,
  Stethoscope,
  ClipboardCheck,
  Users,
  LogOut,
  Leaf,
} from "lucide-react";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@/components/ui/avatar";

const employeeNav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/my-leave", label: "My Leave", icon: Plane },
  { to: "/sick-leave", label: "Sick Leave", icon: Stethoscope },
  { to: "/calendar", label: "Team Calendar", icon: CalendarDays },
];

const managerExtra = [
  { to: "/review", label: "Manager Review", icon: ClipboardCheck },
  { to: "/team", label: "Team", icon: Users },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = [...employeeNav];
  if (user?.role === "manager") nav.push(...managerExtra);

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("");

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#FAF9F6" }}>
      <header
        className="sticky top-0 z-30 bg-white border-b"
        style={{ borderColor: "#EAE8E1" }}
      >
        <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2" data-testid="app-logo">
              <span
                className="grid place-items-center w-8 h-8 rounded-md"
                style={{ backgroundColor: "#647D6C" }}
              >
                <Leaf className="w-4 h-4 text-white" strokeWidth={1.75} />
              </span>
              <span className="font-heading font-semibold text-lg tracking-tight" style={{ color: "#2C332E" }}>
                Verdant HR
              </span>
            </div>
            <nav className="hidden md:flex items-center gap-1">
              {nav.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.end}
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive ? "text-white" : "text-[#6A736D] hover:bg-[#F4F3ED]"
                    }`
                  }
                  style={({ isActive }) =>
                    isActive ? { backgroundColor: "#647D6C" } : {}
                  }
                >
                  <item.icon className="w-4 h-4" strokeWidth={1.75} />
                  {item.label}
                </NavLink>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-medium" style={{ color: "#2C332E" }} data-testid="current-user-name">
                {user?.name}
              </span>
              <span className="text-xs capitalize" style={{ color: "#6A736D" }}>
                {user?.role}
              </span>
            </div>
            <Avatar className="w-9 h-9 border" style={{ borderColor: "#EAE8E1" }}>
              {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user?.name} />}
              <AvatarFallback style={{ backgroundColor: "#647D6C", color: "white" }}>
                {initials}
              </AvatarFallback>
            </Avatar>
            <button
              data-testid="logout-button"
              onClick={() => {
                logout();
                navigate("/login");
              }}
              className="grid place-items-center w-9 h-9 rounded-md border hover:bg-[#F4F3ED] transition-colors"
              style={{ borderColor: "#EAE8E1" }}
              title="Log out"
            >
              <LogOut className="w-4 h-4" style={{ color: "#6A736D" }} strokeWidth={1.75} />
            </button>
          </div>
        </div>
        {/* Mobile nav */}
        <nav className="md:hidden flex items-center gap-1 overflow-x-auto px-3 pb-2">
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition-colors ${
                  isActive ? "text-white" : "text-[#6A736D] bg-[#F4F3ED]"
                }`
              }
              style={({ isActive }) => (isActive ? { backgroundColor: "#647D6C" } : {})}
            >
              <item.icon className="w-3.5 h-3.5" strokeWidth={1.75} />
              {item.label}
            </NavLink>
          ))}
        </nav>
      </header>
      <main className="max-w-6xl mx-auto px-4 md:px-6 py-6 md:py-8 fade-in">{children}</main>
    </div>
  );
}
